import { CombinedUserInfo, EmulatorType, PlannedUsageType } from '@localstack/types';
import { EMULATOR_TYPES } from '@localstack/constants';

import { AuthQueryParamKeys, PlanParamValues } from '../common';

import { VALID_PROMO_CODES } from './constants';
import { emulatorOptions } from './emulators';
import { PlanChoice } from './types';

/** A plan is available when the emulator states terms for it, so entries must be non-empty. */
export const getAvailablePlansForEmulator = (emulator: EmulatorType): PlanChoice[] => {
  const option = emulatorOptions.find((o) => o.value === emulator);
  return Object.values(PlanChoice).filter((plan) => option?.termsByPlan[plan]);
};

export const resolvePlanChoice = (emulator: EmulatorType, picked: PlanChoice, fallback: PlanChoice): PlanChoice => {
  const availablePlans = getAvailablePlansForEmulator(emulator);

  if (availablePlans.includes(picked)) return picked;
  if (availablePlans.includes(fallback)) return fallback;

  return availablePlans[0] ?? fallback;
};

export const canChoosePlanBasedOnEmulatorChoice = (emulator: EmulatorType): boolean =>
  getAvailablePlansForEmulator(emulator).length > 1;

/** The terms to show under an emulator: its own when it allows a single plan, otherwise the picked plan's. */
export const getTermsForEmulator = (emulator: EmulatorType, planChoice: PlanChoice): Optional<string> => {
  const [soloAvailablePlan, ...otherAvailablePlans] = getAvailablePlansForEmulator(emulator);
  const plan = otherAvailablePlans.length ? planChoice : soloAvailablePlan;

  return plan ? emulatorOptions.find((o) => o.value === emulator)?.termsByPlan[plan] : undefined;
};

export const getPromoCodeFromQueryParam = (queryParams: URLSearchParams): string | undefined => {
  const promoCodeFromQueryParam = queryParams.get(AuthQueryParamKeys.PROMO);
  if (!promoCodeFromQueryParam) return;

  return VALID_PROMO_CODES.includes(promoCodeFromQueryParam) ? promoCodeFromQueryParam : undefined;
};

export const getEmulatorTypeFromQueryParams = (queryParams: URLSearchParams): EmulatorType => {
  const emulatorTypeFromQueryParam = queryParams.get(AuthQueryParamKeys.EMULATOR);

  if (emulatorTypeFromQueryParam && EMULATOR_TYPES.includes(emulatorTypeFromQueryParam as EmulatorType)) {
    return emulatorTypeFromQueryParam as EmulatorType;
  }
  return EmulatorType.AWS;
};

export const getPlannedUsageTypeFromQueryParams = (
  queryParams: URLSearchParams,
  userInfo?: Optional<CombinedUserInfo>,
): PlannedUsageType | undefined => {
  const planQueryParam = queryParams.get(AuthQueryParamKeys.PLAN);

  const isFreemiumPlanSignup = planQueryParam === PlanParamValues.FREE;
  const isMarketPlaceSignup = planQueryParam === PlanParamValues.MARKETPLACE;
  const isStudentPlanSignup = planQueryParam === PlanParamValues.STUDENT;

  const passedStudentCheck = !!userInfo?.user.signup_survey?.is_student;

  const isStudentWithVerificationFailed = isStudentPlanSignup && !passedStudentCheck;
  const showFreemiumFlow = isFreemiumPlanSignup || isStudentWithVerificationFailed;

  if (showFreemiumFlow) {
    return PlannedUsageType.FREEMIUM;
  }

  if (isMarketPlaceSignup) {
    return PlannedUsageType.MARKETPLACE;
  }

  return undefined;
};
